import {
  coerceBooleanProperty,
  type BooleanInput,
} from '@angular/cdk/coercion';
import { type ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  CurrentScopeFacade,
  GapStoreService,
  GlobalStoreService,
} from '@principle-theorem/ng-principle-shared';
import { TrackByFunctions } from '@principle-theorem/ng-shared';
import { Event, Gap, getEventTitle } from '@principle-theorem/principle-core';
import {
  AppointmentSummary,
  CalendarEventSummary,
  EVENT_TYPE_COLOUR_MAP,
  IEventStyle,
  IPracticeSettings,
  IScheduleSummaryEventable,
  PreBlockEvent,
  UNCATEGORISED_CATEGORY,
  isAppointmentEventType,
  isAppointmentSummary,
  isCalendarEventSummary,
  isEventable,
  isGapEvent,
  isPreBlockEvent,
  type IDepositMetadata,
  type ITimelineDisplayOptions,
  type PracticeTimelineTooltipDelay,
} from '@principle-theorem/principle-core/interfaces';
import {
  exceedsLuminanceThreshold,
  filterUndefined,
  isWithRef,
  timePeriodWithin,
  type ITimePeriod,
} from '@principle-theorem/shared';
import { colord } from 'colord';
import { isEqual, startCase } from 'lodash';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  combineLatest,
  of,
} from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import {
  isManageEventable,
  type IManageEventable,
} from '../interactive-timeline/interactive-timeline-logic';

type SelectedEventStyle = IEventStyle & { [key: string]: string };

const verticalPositions: ConnectedPosition[] = [
  {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
  },
  {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
  },
  {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
  },
  {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
  },
];

const horizontalPositions: ConnectedPosition[] = [
  {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
  },
  {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
  },
  {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
  },
  {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
  },
];

@Component({
  selector: 'pr-timeline-event-display',
  templateUrl: './timeline-event-display.component.html',
  styleUrls: ['./timeline-event-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TimelineEventDisplayComponent {
  private _options$ = new ReplaySubject<ITimelineDisplayOptions>(1);
  event$ = new ReplaySubject<IScheduleSummaryEventable | IManageEventable>(1);
  eventStyle$: Observable<IEventStyle>;
  range$ = new ReplaySubject<ITimePeriod>(1);
  disabled$ = new BehaviorSubject(false);
  selected$ = new BehaviorSubject(false);
  positions$: Observable<ConnectedPosition[]>;
  eventLabel$: Observable<string>;
  hasTags$: Observable<boolean>;
  tooltipDelay$: Observable<PracticeTimelineTooltipDelay>;
  calendarEvent$: Observable<CalendarEventSummary>;
  isPublicPreBlock$: Observable<boolean>;
  appointment$: Observable<AppointmentSummary>;
  deposit$: Observable<IDepositMetadata | undefined>;
  pendingGap$: Observable<IScheduleSummaryEventable | undefined>;
  pinnedNotes$: Observable<string[]>;
  trackByPinnedNote = TrackByFunctions.variable<string>();

  @Input()
  set selected(selected: BooleanInput) {
    this.selected$.next(coerceBooleanProperty(selected));
  }

  @Input()
  set options(options: ITimelineDisplayOptions) {
    if (options) {
      this._options$.next(options);
    }
  }

  @Input()
  set event(event: IScheduleSummaryEventable | IManageEventable) {
    if (event) {
      this.event$.next(event);
    }
  }

  @Input()
  set range(range: ITimePeriod) {
    if (range) {
      this.range$.next(range);
    }
  }

  @Input()
  set disabled(disabled: BooleanInput) {
    this.disabled$.next(coerceBooleanProperty(disabled));
  }

  constructor(
    private _global: GlobalStoreService,
    private _gapStore: GapStoreService,
    private _currentScope: CurrentScopeFacade
  ) {
    const colourOverrides$ = this._currentScope.currentPractice$.pipe(
      filterUndefined(),
      map((practice) => practice.settings.eventColourOverrides),
      distinctUntilChanged<IPracticeSettings['eventColourOverrides']>(isEqual)
    );

    this.eventStyle$ = this.selected$.pipe(
      switchMap((selected) =>
        combineLatest([this.event$, colourOverrides$]).pipe(
          switchMap(([event, colourOverrides]) =>
            isManageEventable(event)
              ? of(this._getManageEventColour(event, colourOverrides))
              : this._getEventColour$(event, colourOverrides)
          ),
          map((eventStyle) => this._getSelectedEventStyle(eventStyle, selected))
        )
      )
    );

    this.eventLabel$ = this.event$.pipe(
      map((event) => this._getEventLabel(event))
    );

    this.positions$ = this._options$.pipe(
      map((options) => options.orientation),
      distinctUntilChanged(),
      map((orientation) =>
        String(orientation) === 'vertical'
          ? verticalPositions
          : horizontalPositions
      )
    );

    this.tooltipDelay$ = this._options$.pipe(
      map((options) => options.tooltipDelay),
      distinctUntilChanged()
    );

    this.appointment$ = this.event$.pipe(
      filter((event) => isAppointmentSummary(event)),
      map((appointment) => appointment)
    );

    this.deposit$ = this.event$.pipe(
      filter((event) => isAppointmentSummary(event)),
      map((event) => event.metadata.deposit)
    );

    this.calendarEvent$ = this.event$.pipe(
      filter((event) => isCalendarEventSummary(event)),
      filter(isWithRef),
      map((calendarEvent) => calendarEvent)
    );

    this.pendingGap$ = combineLatest([
      this.event$,
      this._gapStore.pendingGaps$,
    ]).pipe(map(([event, gaps]) => this._getPendingGap(event, gaps)));

    this.isPublicPreBlock$ = this.event$.pipe(
      map(
        (event) =>
          !isManageEventable(event) &&
          isPreBlockEvent(event.event) &&
          event.event.isPublic
      )
    );

    this.hasTags$ = this.event$.pipe(
      map((eventable) => {
        if (isManageEventable(eventable)) {
          return [];
        }
        return eventable.metadata.tags;
      }),
      map((tags) => tags.length > 0)
    );

    this.pinnedNotes$ = this.event$.pipe(
      map((eventable) =>
        isManageEventable(eventable) ? [] : eventable.metadata.pinnedNotes
      )
    );
  }

  private _getEventLabel(
    event: IScheduleSummaryEventable | IManageEventable
  ): string {
    if (isEventable(event) && isGapEvent(event.event)) {
      return getEventTitle(event);
    }

    if (isManageEventable(event)) {
      return `New ${startCase(event.event.type)}`;
    }
    return event.metadata.label;
  }

  private _getOverlayColor(backgroundColor: string): string {
    const darkOverlay = 'rgba(0, 0, 0, .6)';
    const lightOverlay = 'rgba(255, 255, 255, .9)';
    return exceedsLuminanceThreshold(backgroundColor, 0.5)
      ? darkOverlay
      : lightOverlay;
  }

  private _getManageEventColour(
    event: IManageEventable,
    colourOverrides?: IPracticeSettings['eventColourOverrides']
  ): IEventStyle {
    const eventColour =
      colourOverrides?.[event.event.type] ??
      EVENT_TYPE_COLOUR_MAP[event.event.type];

    return {
      backgroundColor: eventColour,
      color: this._getOverlayColor(eventColour),
    };
  }

  private _getEventColour$(
    event: IScheduleSummaryEventable,
    colourOverrides?: IPracticeSettings['eventColourOverrides']
  ): Observable<IEventStyle> {
    const eventColour =
      colourOverrides?.[event.event.type] ??
      EVENT_TYPE_COLOUR_MAP[event.event.type];

    if (isPreBlockEvent(event.event)) {
      return this._getPreBlockEventColour$(eventColour, event.event);
    }

    if (!isAppointmentEventType(event.event.type)) {
      return of(this._createEventStyle(eventColour));
    }

    const appointmentSummary = event as AppointmentSummary;

    if (!appointmentSummary.metadata.categoryRef) {
      return of(this._createEventStyle(UNCATEGORISED_CATEGORY.colour.value));
    }

    return this._global
      .getTreatmentCategory$(appointmentSummary.metadata.categoryRef)
      .pipe(
        map((category) =>
          category ? category.colour.value : UNCATEGORISED_CATEGORY.colour.value
        ),
        map((backgroundColor) => this._createEventStyle(backgroundColor))
      );
  }

  private _getSelectedEventStyle(
    eventStyles: IEventStyle,
    selected: boolean
  ): SelectedEventStyle {
    if (!selected) {
      return { ...eventStyles };
    }
    const colour = colord(eventStyles.backgroundColor);
    const output =
      colour.luminance() > 0.5
        ? colour.darken(0.12).toRgbString()
        : colour.lighten(0.12).toRgbString();

    return {
      ...this._createEventStyle(output),
    };
  }

  private _getPreBlockEventColour$(
    defaultColour: string,
    event: PreBlockEvent
  ): Observable<IEventStyle> {
    const defaultOpacity = colord(defaultColour).alpha();
    if (event.colourOverride) {
      return of(
        this._createEventStyle(
          colord(event.colourOverride).alpha(defaultOpacity).toHex()
        )
      );
    }

    if (event.allowedTreatmentCategories.length === 1) {
      return this._global
        .getTreatmentCategory$(event.allowedTreatmentCategories[0])
        .pipe(
          map((category) => {
            const backgroundColor = category
              ? colord(category.colour.value).alpha(defaultOpacity).toHex()
              : defaultColour;
            return this._createEventStyle(backgroundColor);
          })
        );
    }
    return of(this._createEventStyle(defaultColour));
  }

  private _createEventStyle(backgroundColor: string): IEventStyle {
    const darkOverlay = 'rgba(0, 0, 0, .6)';
    const lightOverlay = 'rgba(255, 255, 255, .9)';

    return {
      backgroundColor,
      color: exceedsLuminanceThreshold(backgroundColor, 0.5)
        ? darkOverlay
        : lightOverlay,
    };
  }

  private _getPendingGap(
    { event }: IScheduleSummaryEventable | IManageEventable,
    pendingGaps: IScheduleSummaryEventable[]
  ): IScheduleSummaryEventable | undefined {
    const foundPendingGap = Gap.findPendingGapFromEvent(event, pendingGaps);
    if (!foundPendingGap) {
      return;
    }

    const candidates =
      foundPendingGap.metadata.candidates?.filter((candidate) =>
        timePeriodWithin(
          Event.toTimePeriod(candidate.event),
          Event.toTimePeriod(event),
          true
        )
      ) ?? [];

    return {
      ...foundPendingGap,
      metadata: {
        ...foundPendingGap.metadata,
        candidates,
      },
    };
  }
}
